import { Trans, useTranslation } from 'react-i18next'

import illustration from '@/assets/images/illustrations/simulateur.svg'
import PageHeader from '@/components/PageHeader'
import { ACCUEIL, TrackPage } from '@/components/PianoAnalytics'
import { SimulateurCard } from '@/components/SimulateurCard'
import { SmallSimulateurCard } from '@/components/SmallSimulateurCard'
import Meta from '@/components/utils/Meta'
import {
	Body,
	Grid,
	H2,
	H3,
	Intro,
	Li,
	Link,
	Strong,
	Ul,
} from '@/design-system'
import { useSimulatorsMetadata } from '@/hooks/useSimulatorsMetadata'
import { useSitePaths } from '@/sitePaths'

export default function SimulateursEtAssistants() {
	const { t } = useTranslation()
	const { absoluteSitePaths } = useSitePaths()
	const simulators = useSimulatorsMetadata()
	const titre = t(
		'pages.simulateurs.accueil.titre',
		'Simulateurs et Assistants'
	)

	return (
		<>
			<TrackPage chapter1="simulateurs" name={ACCUEIL} />
			<Meta
				title={titre}
				description={t(
					'pages.simulateurs.accueil.description',
					'Tous les simulateurs et assistants sur ce site sont maintenus à jour avec les dernières évolutions législatives.'
				)}
				openGraph={{ image: illustration }}
			/>
			<PageHeader titre={titre} picture={illustration}>
				<Intro>
					{t(
						'pages.simulateurs.accueil.header',
						'Tous les simulateurs sur ce site sont maintenus à jour avec les dernières évolutions législatives.'
					)}
				</Intro>
			</PageHeader>
			<section>
				<H2 id="salarie-employeurs">
					{t('pages.simulateurs.accueil.1.h2', 'Salariés et employeurs')}
				</H2>
				<Grid
					role="list"
					container
					spacing={3}
					aria-labelledby="salarie-employeurs"
				>
					<SimulateurCard role="listitem" {...simulators.salarié} />
					<SimulateurCard
						role="listitem"
						{...simulators['activité-partielle']}
					/>
				</Grid>

				<H3 id="revenu-dirigeant">
					{t(
						'pages.simulateurs.accueil.1.h3',
						'Revenu du dirigeant par statut'
					)}
				</H3>
				<Grid
					container
					spacing={3}
					role="list"
					aria-labelledby="revenu-dirigeant"
				>
					<SmallSimulateurCard
						role="listitem"
						{...simulators['auto-entrepreneur']}
					/>
					<SmallSimulateurCard
						role="listitem"
						{...simulators['entreprise-individuelle']}
					/>
					<SmallSimulateurCard role="listitem" {...simulators.sasu} />
					<SmallSimulateurCard role="listitem" {...simulators.eurl} />
					<SmallSimulateurCard
						role="listitem"
						{...simulators['comparaison-statuts']}
					/>
				</Grid>

				<H2 id="travailleurs-ns">
					{t(
						'pages.simulateurs.accueil.2.h2',
						'Travailleurs et travailleuses Non Salariés (TNS)'
					)}
				</H2>

				<Grid
					container
					spacing={3}
					role="list"
					aria-labelledby="travailleurs-ns"
				>
					<SimulateurCard role="listitem" {...simulators.indépendant} />
					<SimulateurCard role="listitem" {...simulators['artiste-auteur']} />
					<SimulateurCard
						role="listitem"
						{...simulators['profession-libérale']}
					/>
				</Grid>
				<>
					<H3 id="professions-lib">
						{t('pages.simulateurs.accueil.2.h3.1', 'Professions libérales')}
					</H3>
					<Grid
						container
						spacing={3}
						role="list"
						aria-labelledby="professions-lib"
					>
						<SmallSimulateurCard
							role="listitem"
							{...simulators['auxiliaire-médical']}
						/>
						<SmallSimulateurCard
							role="listitem"
							{...simulators['chirurgien-dentiste']}
						/>
						<SmallSimulateurCard role="listitem" {...simulators.médecin} />
						<SmallSimulateurCard
							role="listitem"
							{...simulators['sage-femme']}
						/>
						<SmallSimulateurCard role="listitem" {...simulators.pharmacien} />
						<SmallSimulateurCard role="listitem" {...simulators.avocat} />
						<SmallSimulateurCard
							role="listitem"
							{...simulators['expert-comptable']}
						/>
						<SmallSimulateurCard role="listitem" {...simulators.cipav} />
					</Grid>
				</>

				<H2 id="autres-outils">
					{t('pages.simulateurs.accueil.3.h2', 'Autres outils')}
				</H2>
				<Grid container spacing={3} role="list" aria-labelledby="autres-outils">
					<SimulateurCard role="listitem" {...simulators['choix-statut']} />
					<SimulateurCard role="listitem" {...simulators.is} />
					<SimulateurCard role="listitem" {...simulators.dividendes} />
					<SimulateurCard
						role="listitem"
						{...simulators['location-de-logement-meublé']}
					/>
					{/* <SimulateurCard
						role="listitem"
						{...simulators['cotisation-maladie-frontalier-suisse']}
					/> */}

					<SimulateurCard
						{...simulators['coût-création-entreprise']}
						role="listitem"
					/>
					<SimulateurCard
						{...simulators['recherche-code-ape']}
						role="listitem"
					/>
					<SimulateurCard
						{...simulators['cessation-activité']}
						role="listitem"
					/>
				</Grid>
			</section>
			<section>
				<Trans i18nKey="pages.simulateurs.accueil.section">
					<Body>Tous les simulateurs sur ce site sont :</Body>
					<Ul>
						<Li>
							<strong>Maintenus à jour</strong> avec les dernières évolutions
							législatives
						</Li>
						<Li>
							<strong>Améliorés en continu</strong> afin d'augmenter le nombre
							de dispositifs pris en compte
						</Li>
						<Li>
							<Strong>Intégrables facilement et gratuitement</Strong> sur
							n'importe quel site internet.{' '}
							<Link to={absoluteSitePaths.développeur.iframe}>
								En savoir plus
							</Link>
							.
						</Li>
					</Ul>
				</Trans>
			</section>
		</>
	)
}
